/**
 * Authentication Field Validation Rules
 *
 * Centralized validation rules for all auth forms (Login, CreateAccount, ForgotPassword).
 * Using react-hook-form validation format.
 *
 * @see https://react-hook-form.com/api/useform/register
 */

import { isValidPhoneNumber } from 'react-phone-number-input';

/**
 * Email validation rules
 * Used in: Login, CreateAccount, ForgotPassword
 */
export const EMAIL_VALIDATION = {
  required: 'Email is required',
  maxLength: {
    value: 254,
    message: 'Email must be no more than 254 characters'
  },
  pattern: {
    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  }
};

/**
 * Password validation for NEW passwords (account creation)
 * Used in: CreateAccount
 *
 * Requirements (matches shopping-ui standards):
 * - At least 10 and at most 128 characters
 * - Upper case letters (A-Z)
 * - Lower case letters (a-z)
 * - Numbers (0-9)
 * - Symbols (!@#$%&*)
 */
export const NEW_PASSWORD_VALIDATION = {
  required: 'Password is required',
  minLength: {
    value: 10,
    message: 'Password must be at least 10 characters'
  },
  maxLength: {
    value: 128,
    message: 'Password must be no more than 128 characters'
  },
  validate: (value) => {
    // Check for uppercase letter
    if (!/[A-Z]/.test(value)) {
      return 'Password must contain at least one uppercase letter (A-Z)';
    }
    // Check for lowercase letter
    if (!/[a-z]/.test(value)) {
      return 'Password must contain at least one lowercase letter (a-z)';
    }
    // Check for number
    if (!/[0-9]/.test(value)) {
      return 'Password must contain at least one number (0-9)';
    }
    // Check for symbol (non-letter, non-digit, non-space character)
    if (!/[^\p{L}\d\s]/u.test(value)) {
      return 'Password must contain at least one symbol (!@#$%&*)';
    }
    return true;
  }
};

/**
 * Password validation for EXISTING passwords (login)
 * Used in: Login
 * Note: No pattern validation - user might have old password that doesn't meet current rules
 */
export const CURRENT_PASSWORD_VALIDATION = {
  required: 'Password is required'
};

/**
 * First name validation
 * Used in: CreateAccount
 */
export const FIRST_NAME_VALIDATION = {
  required: 'First name is required',
  maxLength: {
    value: 50,
    message: 'First name must be no more than 50 characters'
  },
  pattern: {
    value: /^[a-zA-Z\s'-]+$/,
    message: 'First name can only contain letters, spaces, hyphens, and apostrophes'
  }
};

/**
 * Last name validation
 * Used in: CreateAccount
 */
export const LAST_NAME_VALIDATION = {
  required: 'Last name is required',
  maxLength: {
    value: 50,
    message: 'Last name must be no more than 50 characters'
  },
  pattern: {
    value: /^[a-zA-Z\s'-]+$/,
    message: 'Last name can only contain letters, spaces, hyphens, and apostrophes'
  }
};

/**
 * Phone validation
 * Used in: CreateAccount
 * Uses react-phone-number-input's built-in validation
 */
export const PHONE_VALIDATION = {
  required: 'Phone number is required',
  validate: (value) => {
    if (!value) return true; // Let required handle empty values
    return isValidPhoneNumber(value) || 'Please enter a valid phone number';
  }
};

/**
 * Consolidated validation object for easy access
 *
 * @example
 * import { AUTH_VALIDATIONS } from './constants/validations';
 *
 * <TextField {...register('email', AUTH_VALIDATIONS.email)} />
 */
export const AUTH_VALIDATIONS = Object.freeze({
  email: EMAIL_VALIDATION,
  newPassword: NEW_PASSWORD_VALIDATION,
  currentPassword: CURRENT_PASSWORD_VALIDATION,
  firstName: FIRST_NAME_VALIDATION,
  lastName: LAST_NAME_VALIDATION,
  phone: PHONE_VALIDATION
});

/**
 * Helper function to get validation by field name
 *
 * @param {string} fieldName - Name of the field
 * @returns {object} Validation rules for the field
 *
 * @example
 * getValidation('email') // Returns EMAIL_VALIDATION
 */
export function getValidation(fieldName) {
  return AUTH_VALIDATIONS[fieldName] || {};
}
